import { apiClient } from '../../utils/axiosWithAuth';
import {
  MenuCategory,
  MenuItem,
  CreateMenuCategoryDto,
  UpdateMenuCategoryDto,
  CreateMenuItemDto,
  UpdateMenuItemDto,
  MenuQueryDto,
  MenuItemsResponse,
  ImportMenuResult,
} from './types/menu.types';

// Safely unwrap API responses that may be wrapped as { status, message, data }
const unwrapResponse = <T>(response: any): T => {
  if (response && typeof response === 'object') {
    if (response.data && typeof response.data === 'object') {
      return (response.data as any).data !== undefined
        ? (response.data as any).data
        : (response.data as T);
    }
  }
  return response as T;
};

export const menuAPI = {
  // ==================== MENU CATEGORIES ====================
  
  getCategories: async (): Promise<MenuCategory[]> => {
    const response = await apiClient.get('/menu/categories');
    const payload = unwrapResponse<MenuCategory[]>(response);
    return Array.isArray(payload) ? payload : [];
  },

  getCategory: async (uuid: string): Promise<MenuCategory> => {
    const response = await apiClient.get(`/menu/categories/${uuid}`);
    return unwrapResponse<MenuCategory>(response);
  },

  createCategory: async (data: CreateMenuCategoryDto): Promise<MenuCategory> => {
    const { image, ...rest } = data;
    let response;
    if (image instanceof File) {
      const formData = new FormData();
      formData.append('image', image);
      Object.entries(rest).forEach(([key, value]) => {
        if (value !== undefined && value !== null) formData.append(key, String(value));
      });
      response = await apiClient.post('/menu/categories', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } else {
      response = await apiClient.post('/menu/categories', rest);
    }
    return unwrapResponse<MenuCategory>(response);
  },

  updateCategory: async (
    uuid: string,
    data: UpdateMenuCategoryDto,
  ): Promise<MenuCategory> => {
    const { image, ...rest } = data;
    let response;
    if (image instanceof File) {
      const formData = new FormData();
      formData.append('image', image);
      Object.entries(rest).forEach(([key, value]) => {
        if (value !== undefined && value !== null) formData.append(key, String(value));
      });
      response = await apiClient.put(`/menu/categories/${uuid}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } else {
      response = await apiClient.put(`/menu/categories/${uuid}`, rest);
    }
    return unwrapResponse<MenuCategory>(response);
  },

  deleteCategory: async (uuid: string): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/menu/categories/${uuid}`);
    return unwrapResponse<{ message: string }>(response);
  },

  // ==================== MENU ITEMS ====================

  getMenuItems: async (query: MenuQueryDto): Promise<MenuItemsResponse> => {
    const response = await apiClient.get('/menu/items', { params: query });
    const payload = unwrapResponse<MenuItemsResponse | MenuItem[]>(response);

    if (Array.isArray(payload)) {
      const items = payload as MenuItem[];
      return {
        items,
        total: items.length,
        page: 1,
        limit: items.length || query.limit || 20,
        totalPages: 1,
      };
    }

    return payload as MenuItemsResponse;
  },

  getMenuItem: async (uuid: string): Promise<MenuItem> => {
    const response = await apiClient.get(`/menu/items/${uuid}`);
    return unwrapResponse<MenuItem>(response);
  },

  createMenuItem: async (data: CreateMenuItemDto | FormData): Promise<MenuItem> => {
    const isFormData = data instanceof FormData;
    const response = await apiClient.post('/menu/items', data, isFormData ? {
      headers: { 'Content-Type': 'multipart/form-data' },
    } : undefined);
    return unwrapResponse<MenuItem>(response);
  },

  updateMenuItem: async (
    uuid: string,
    data: UpdateMenuItemDto | FormData,
  ): Promise<MenuItem> => {
    const isFormData = data instanceof FormData;
    const response = await apiClient.put(`/menu/items/${uuid}`, data, isFormData ? {
      headers: { 'Content-Type': 'multipart/form-data' },
    } : undefined);
    return unwrapResponse<MenuItem>(response);
  },

  deleteMenuItem: async (uuid: string): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/menu/items/${uuid}`);
    return unwrapResponse<{ message: string }>(response);
  },

  // ==================== BULK IMPORT ====================

  importMenuItems: async (file: File, updateIfExists: boolean = false): Promise<ImportMenuResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('updateIfExists', String(updateIfExists));

    const response = await apiClient.post('/menu/items/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return unwrapResponse<ImportMenuResult>(response);
  },
};
